import { useCallback, useMemo } from "react";
import { useAuthCtx } from "../../Auth/useAuthCtx.ts";
import { getRolesFromAuth0User } from "../../Auth/getRolesFromAuth0User.ts";
import { KnownAuth0Roles } from "../../Auth/KnownAuth0Roles.ts";
import { useAllNormasTeachingProducts } from "../NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useAllNormasTeachingBundles } from "../NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import {
  PurchasedItemRef,
  ResolvedPurchasedItemName,
  resolvePurchasedItemName,
} from "./resolvePurchasedItemName.ts";

export function usePurchasedItemNames(): {
  resolveName: (item: PurchasedItemRef) => ResolvedPurchasedItemName;
  isLoading: boolean;
} {
  const { user } = useAuthCtx();

  const isAdmin = useMemo(
    () =>
      user
        ? getRolesFromAuth0User(user).includes(KnownAuth0Roles.Admin)
        : false,
    [user],
  );

  const { products, isLoading: isLoadingProducts } =
    useAllNormasTeachingProducts({ enabled: isAdmin });
  const { bundles, isLoading: isLoadingBundles } = useAllNormasTeachingBundles({
    enabled: isAdmin,
  });

  const maps = useMemo(
    () => ({
      productNameById: Object.fromEntries(
        products.map((p) => [p._id, p.variantProductName]),
      ),
      bundleNameById: Object.fromEntries(
        bundles.map((b) => [b._id, b.variantBundleName]),
      ),
    }),
    [products, bundles],
  );

  const resolveName = useCallback(
    (item: PurchasedItemRef) => resolvePurchasedItemName(item, maps),
    [maps],
  );

  return {
    resolveName,
    isLoading: isLoadingProducts || isLoadingBundles,
  };
}
